/**
 * The `lg`-and-up layout of `../ExploreOtherCapabilitiesSolutions.tsx`: the section title as an
 * `h1`, then one bordered row per card (image, linked heading with its badge, description), then
 * the "Learn more" link. Mirrors `../Mobile/ExploreOtherCapabilitesMobile.tsx`, which has no
 * title and uses the primary `ButtonLink` type.
 */
import React from "react";
import styled from "@emotion/styled";
import rgba from "polished/lib/color/rgba";
import { useTheme } from "@ryerson/frontend.theme";
import { ContentSection } from "@ryerson/frontend.layout";
import { Typography } from "@ryerson/frontend.typography";
import { Flex, FlexItem } from "@ryerson/frontend.layout";
import { css } from "@emotion/react";
import { Badge } from "@ryerson/frontend.notification";
import { Link, ButtonLink } from "@ryerson/frontend.navigation";

const ButtonLinkContainer = styled.div`
	display: block;
	width: 100%;
	margin-top: 60px;
	margin-bottom: 110px;
`;

const ContentSectionImage = styled.img`
	width: 100%;
	margin-bottom: 20px;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media only screen and (min-width: ${theme.breakpoints.lg}) {
				width: 300px;
				margin: 0;
		`;
	}}
`;
// `background-color` below is literal text, not an interpolation, so the browser discards it as
// an invalid value.
const ContentSectionText = styled.div`
	width: 100%;
	background-color: theme.colors.primary.secondary;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media only screen and (min-width: ${theme.breakpoints.lg}) {
				width: 360px;
			}
		`;
	}}
`;
const CustomBadge = styled(Badge)`
	background-color: #fff;
`;
const Header = styled(Typography)`
	padding-top: 80px;
	padding-bottom: 60px;
`;
const Divider = styled.div`
	padding-top: 15px;
	width: 100%;
	opacity: 0.1;
	border-bottom: 1px solid black;
`;
/**
 * Desktop explore-other cards. Takes `ExploreOtherCapabilitiesSolutionsProps` untyped. Each image's
 * `alt` reads `section.title`, a field the section type doesn't have, so the images render with
 * no `alt` attribute.
 */
const ExploreOtherCapabilitiesSolutionsDesktop = ({ ...props }) => {
	const { theme } = useTheme();
	const { section, title } = props;
	const content = section;
	const contentSectionTitle = css`
		padding: 10px 0px;
		@media only screen and (min-width: ${theme.breakpoints.lg}) {
			width: 260px;
			padding: 0px;
			h3 {
				padding: 0px;
			}
		}
		h3 {
			margin: 0px;
			padding-bottom: 10px;
		}
	`;
	const alignBadge = css`
		@media only screen and (max-width: ${theme.breakpoints.lg}) {
			position: absolute;
			top: 60px;
			left: 20px;
		}
	`;
	const addFlex = css`
		border: none;
		padding: 30px 0;
		position: relative;
		@media only screen and (min-width: ${theme.breakpoints.lg}) {
			flex-direction: row;
			border-bottom: 1px solid ${rgba(theme.colors.primary.darkGray, 0.1)};
			padding: 40px 0;
		}
	`;
	return (
		<ContentSection vPadding="0" hPadding="0" type="secondary">
			<Header variant="h1">{title}</Header>
			<Divider />
			{content.map((section: any, index: number) => (
				<Flex justifyContent="space-between" direction="column" style={addFlex} key={index}>
					<ContentSectionImage theme={theme} src={section.imageUrl} alt={section.title} />
					<Flex
						direction="column"
						justifyContent="space-between"
						style={contentSectionTitle}
					>
						<Link to={section.linkUrl}>
							<Typography variant="h3">{section.mainText}</Typography>
						</Link>
						<FlexItem css={alignBadge}>
							<CustomBadge
								color={
									section.badgeColor === "success"
										? theme.colors.primary.success
										: section.badgeColor === "error"
										? theme.colors.primary.error
										: section.badgeColor === "industry"
										? theme.colors.primary.secondaryBrand
										: theme.colors.secondary.orange
								}
							>
								{section.badgeText}
							</CustomBadge>
						</FlexItem>
					</Flex>
					<ContentSectionText theme={theme}>
						<Typography>{section.subText}</Typography>
					</ContentSectionText>
				</Flex>
			))}
			<ButtonLinkContainer>
				<ButtonLink
					to={props.learnMoreUrl}
					label={props.learnMoreText}
					orientation="left"
					type="secondary"
				/>
			</ButtonLinkContainer>
		</ContentSection>
	);
};

export default ExploreOtherCapabilitiesSolutionsDesktop;
